// src/ui/layout/AppShell.jsx
import React, { useState } from "react";
import Counters from "../misc/Counters";
import ThemeToggle from "../misc/ThemeToggle";
import { LogoIcon, ShareIcon, DiceframeIcon, SupportIcon } from "../../primatives/Icons";
import AdSlot from "../misc/AdSlot";
import ShareModal from "../share/ShareModal";
import CheckoutStatusModal from "../../features/components/CheckoutSuccessModal";

import AuthModal from "../auth/AuthModal";
import AccountMenu from "../auth/AccountMenu"; 
import { useApp } from "../../../state/Context";

export default function AppShell({ children, onChangeSelection }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const { setSelection } = useApp();

  const ENABLE_REFERRALS = false;
  const ENABLE_ADS = false;

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 dark:bg-neutral-950 dark:text-neutral-50">
      <header className="sticky top-0 z-10 border-b border-neutral-200/70 bg-white/80 backdrop-blur dark:border-neutral-800/70 dark:bg-neutral-950/70">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <button
            type="button"
            onClick={onChangeSelection}
            className="
              flex items-center gap-3 rounded-lg text-left
              focus:outline-none focus:ring-2 focus:ring-neutral-300
              dark:focus:ring-neutral-700
            "
            aria-label="Go to selection screen"
            title="Go to selection screen"
          >
            <LogoIcon />
            <div className="leading-tight">
              <div className="text-lg font-semibold tracking-tight sm:text-xl">
                Degree Classify UK
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-100 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100 dark:hover:bg-neutral-900"
              aria-label="Share"
              title="Share"
            >
              <ShareIcon />
            </button>

            <ThemeToggle />

            {/* Account dropdown */}
            <AccountMenu
              onOpenSignIn={() => setAuthOpen(true)}
              onOpenTimeline={() => setSelection((prev) => ({ ...prev, step: "timeline" }))}
              onOpenHow={() => setSelection((prev) => ({ ...prev, step: "how" }))}
              onOpenTerms={() => setSelection((prev) => ({ ...prev, step: "terms" }))}
              onOpenPrivacy={() => setSelection((prev) => ({ ...prev, step: "privacy" }))}
              onOpenDonate={() =>
                window.open(
                  "https://www.paypal.com/donate/?hosted_button_id=UHUNQVDR2S2X4",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              onOpenContact={() => {
                window.location.href = "mailto:support@diceframe.co.uk";
              }}
            />
          </div>
        </div>
      </header>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} brandName="DegreeClassifyUK" />

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title="Broadcast the URL"
        enableReferrals={ENABLE_REFERRALS}
      />

      <div className="border-b border-neutral-200/70 dark:border-neutral-800/70 dark:bg-neutral-950">
        <div className="mx-auto max-w-6xl px-4 py-3">
          <Counters />
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-4 py-6">
        {ENABLE_ADS && (
          <div className="mb-1 lg:hidden">
            <AdSlot label="Sponsored" size="mobile" />
          </div>
        )}

        <div
          className={[
            "grid grid-cols-1 gap-6",
            ENABLE_ADS
              ? "lg:grid-cols-[1fr_minmax(0,72rem)_1fr]"
              : "lg:grid-cols-1 lg:justify-items-center",
          ].join(" ")}
        >
          {ENABLE_ADS && (
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-4">
                <AdSlot size="sidebar" />
              </div>
            </aside>
          )}

          <main className={ENABLE_ADS ? "min-w-0" : "min-w-0 w-full max-w-[72rem]"}>
            {children}
          </main>

          {ENABLE_ADS && (
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-4">
                <AdSlot size="sidebar" />
              </div>
            </aside>
          )}
        </div>
      </div>

      <footer className="border-t border-neutral-200/70 py-4 text-sm text-neutral-600 dark:border-neutral-800/70 dark:text-neutral-400">
        <div className="mx-auto max-w-6xl px-4">
          {/* Stack on mobile, row on sm+ */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {/* Left */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <LogoIcon className="h-4 w-4 shrink-0 opacity-70 text-neutral-600 dark:text-neutral-400" />
                <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
                  <span>Degree Classify UK © {new Date().getFullYear()}</span>
                </div>
              </div>

              <span className="hidden text-neutral-300 dark:text-neutral-600 sm:inline">|</span>

              <div className="flex items-center gap-1 text-neutral-500 dark:text-neutral-400">
                <DiceframeIcon className="h-4 w-4 shrink-0 opacity-70" />
                <span>Diceframe Digital</span>
              </div>

              <span className="hidden text-neutral-300 dark:text-neutral-600 sm:inline">|</span>

              <div className="flex items-center gap-1 text-neutral-500 dark:text-neutral-400">
                <SupportIcon className="h-4 w-4 shrink-0 opacity-70" />
                <span>support@diceframe.co.uk</span>
              </div>
            </div>

            {/* Right */}
            <nav
              aria-label="Footer"
              className="flex flex-wrap items-center gap-x-5 gap-y-2"
            >
              <button
                onClick={() => setSelection((p) => ({ ...p, step: "how" }))}
                className="hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                How it’s done
              </button>

              <button
                onClick={() => setSelection((p) => ({ ...p, step: "timeline" }))}
                className="hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                Timeline
              </button>

              <a
                href="https://www.paypal.com/donate/?hosted_button_id=UHUNQVDR2S2X4"
                className="hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                Donate
              </a>

              <button
                onClick={() => setSelection((p) => ({ ...p, step: "terms" }))}
                className="hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                Terms
              </button>

              <button
                onClick={() => setSelection((p) => ({ ...p, step: "privacy" }))}
                className="hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                Privacy
              </button>

              <a
                href="mailto:support@diceframe.co.uk"
                className="hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                Contact
              </a>
            </nav>
          </div>
        </div>
      </footer>
      <CheckoutStatusModal />
    </div>
  );
}
